/**
 * ApprovalsLanding — three-tab landing for RAP.
 *
 *   1. Pending  — live cards via Microsoft Graph Approvals
 *      (or mock dataset until Approval.ReadWrite consent lands in AAD).
 *      Approve / Reject inline; View detail opens a read-only modal.
 *   2. History  — last-30-day decisions; same card-style detail view.
 *   3. Config   — Manager+ tab with reason templates, auto-approve thresholds,
 *      SLA targets, plus the legacy CD waiver / cart archive matrices.
 *
 * Bill 2026-04-24 mandate: PCB does NOT punt approvers out to Teams. The
 * old "open Microsoft Teams → Approvals" copy is gone everywhere.
 */

import { useMemo, useState } from 'react';
import { BadgeCheck, Clock, Settings, CheckCircle2 } from 'lucide-react';
import { Button, KpiTile } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useEffect, useRef } from 'react';
import { ApprovalCard, ApprovalCardSkeleton } from './ApprovalCard';
import { ApprovalDecisionModal } from './ApprovalDecisionModal';
import { ApprovalDetailModal, ApprovalsHistory } from './ApprovalsHistory';
import { ApprovalsConfig } from './ApprovalsConfig';
import { useApprovalsList } from './hooks/useApprovalsList';
import { isApprovalsLiveEnabled } from './services/graphApprovalsClient';
import type { GraphApproval, ApprovalDecision } from './services/graphApprovalsClient';
import './ApprovalsLanding.css';

type TabKey = 'pending' | 'history' | 'config';

const CONFIG_ALLOWED_ROLES = new Set(['Manager', 'Executive', 'SuperAdmin', 'Admin']);

export function ApprovalsLanding(): JSX.Element {
  const { user } = useAuth();
  const role = user?.primaryRole ?? 'Officer';
  const showConfig = CONFIG_ALLOWED_ROLES.has(role);
  const [tab, setTab] = useState<TabKey>('pending');

  return (
    <div>
      <header
        style={{
          marginBottom: 'var(--sp-6)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <h1 style={{ margin: 0 }}>Approvals</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>
          Microsoft Teams Approvals, surfaced and actioned right here. Approve, reject, and
          review without leaving the app.
        </p>
      </header>

      <LatencyKpiStrip />

      <nav className="rap-tabs" aria-label="Approvals sections">
        <PendingTabButton active={tab === 'pending'} onClick={() => setTab('pending')} />
        <button
          type="button"
          className={`rap-tab ${tab === 'history' ? 'is-active' : ''}`}
          onClick={() => setTab('history')}
          aria-pressed={tab === 'history'}
        >
          <Clock size={14} aria-hidden /> History
        </button>
        {showConfig && (
          <button
            type="button"
            className={`rap-tab ${tab === 'config' ? 'is-active' : ''}`}
            onClick={() => setTab('config')}
            aria-pressed={tab === 'config'}
          >
            <Settings size={14} aria-hidden /> Config
          </button>
        )}
      </nav>

      {tab === 'pending' && <PendingTab />}
      {tab === 'history' && <ApprovalsHistory />}
      {tab === 'config' && showConfig && <ApprovalsConfig managerView />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending tab — live cards
// ---------------------------------------------------------------------------

function PendingTab(): JSX.Element {
  const { approvals, loading, error, isLive, fallbackReason, refresh, patchApproval, removeApproval } =
    useApprovalsList({ status: 'pending', top: 100 });
  const [pendingDecision, setPendingDecision] = useState<{
    approval: GraphApproval;
    decision: ApprovalDecision;
  } | null>(null);
  const [detailFor, setDetailFor] = useState<GraphApproval | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pending = useMemo<ReadonlyArray<GraphApproval>>(
    () => approvals.filter((a) => a.overallStatus === 'pending'),
    [approvals],
  );

  const handleSuccess = (
    approval: GraphApproval,
    decision: ApprovalDecision,
    comment: string,
    isLiveResp: boolean,
  ): void => {
    // Optimistic patch — flip overallStatus locally and remove from pending.
    patchApproval(approval.id, {
      overallStatus: decision === 'approve' ? 'approved' : 'rejected',
      stages: approval.stages.map((s) =>
        s.id === approval.activeStageId
          ? {
              ...s,
              status: 'completed',
              reviewResult: decision === 'approve' ? 'approve' : 'deny',
              justification: comment || s.justification || null,
              reviewedDateTime: new Date().toISOString(),
            }
          : s,
      ),
      activeStageId: null,
    });
    removeApproval(approval.id);
    setToast(
      isLiveResp
        ? `${decision === 'approve' ? 'Approved' : 'Rejected'} — synced to Microsoft Teams Approvals.`
        : `${decision === 'approve' ? 'Approved' : 'Rejected'} — saved locally; live sync activates after AAD consent.`,
    );
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-5)',
        }}
      >
        <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-500)' }}>
          {isLive ? 'Live · Microsoft Graph' : 'Mock dataset'}
          {isApprovalsLiveEnabled() ? '' : ' · feature flag off'}
        </span>
      </div>

      {fallbackReason && !isLive && (
        <div className="rap-source-banner is-warn" role="status">
          <span className="rap-source-banner__dot" aria-hidden />
          <span>
            Approvals integration coming online — admin needs to grant <code>Approval.ReadWrite</code>{' '}
            consent on the AAD app registration. Currently rendering a representative dataset.
          </span>
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {toast && (
        <div
          role="status"
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            background: 'var(--ok-bg)',
            color: 'var(--ok-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {toast}
        </div>
      )}

      {loading ? (
        <div className="rap-grid">
          <ApprovalCardSkeleton />
          <ApprovalCardSkeleton />
          <ApprovalCardSkeleton />
        </div>
      ) : pending.length === 0 ? (
        <div className="rap-empty">
          <div className="rap-empty__icon" aria-hidden>
            <CheckCircle2 size={28} />
          </div>
          <h2 className="rap-empty__title">All caught up — no approvals waiting on you.</h2>
          <p className="rap-empty__msg">
            New requests routed to you will appear here automatically. The rail badge updates in
            real time so you'll see them the moment they arrive.
          </p>
        </div>
      ) : (
        <div className="rap-grid">
          {pending.map((a) => (
            <ApprovalCard
              key={a.id}
              approval={a}
              onApprove={(approval) => setPendingDecision({ approval, decision: 'approve' })}
              onReject={(approval) => setPendingDecision({ approval, decision: 'reject' })}
              onViewDetail={(approval) => setDetailFor(approval)}
            />
          ))}
        </div>
      )}

      <ApprovalDecisionModal
        open={pendingDecision !== null}
        approval={pendingDecision?.approval ?? null}
        decision={pendingDecision?.decision ?? null}
        onClose={() => setPendingDecision(null)}
        onSuccess={handleSuccess}
      />

      {detailFor && (
        <ApprovalDetailModal approval={detailFor} onClose={() => setDetailFor(null)} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pending tab button (lifted so it can read the badge state)
// ---------------------------------------------------------------------------

import { useApprovalsBadge } from './hooks/useApprovalsBadge';

function PendingTabButton({ active, onClick }: { active: boolean; onClick: () => void }): JSX.Element {
  const { count, urgent } = useApprovalsBadge();
  return (
    <button
      type="button"
      className={`rap-tab ${active ? 'is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <BadgeCheck size={14} aria-hidden /> Pending
      {count > 0 && (
        <span className={`rap-tab__count ${urgent ? 'is-urgent' : ''}`}>{count}</span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Latency KPI strip — kept from the original RAP module (still useful)
// ---------------------------------------------------------------------------

function LatencyKpiStrip(): JSX.Element {
  const apiClient = useApiClient();
  const [kpis, setKpis] = useState<Record<string, number> | null>(null);
  const [loadErr, setLoadErr] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void (async () => {
      try {
        const resp = await apiClient.retail.metrics.dashboard({ days: 30 }, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (resp.success) {
          setKpis((resp.kpis as unknown as Record<string, number>) ?? null);
        } else {
          setLoadErr(true);
        }
      } catch {
        if (!controller.signal.aborted) setLoadErr(true);
      }
    })();
    return () => abortRef.current?.abort();
  }, [apiClient]);

  const fmt = (n: number | undefined): string => {
    if (n === undefined) return '—';
    if (n < 60) return `${n.toFixed(0)} min`;
    return `${(n / 60).toFixed(1)} hr`;
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 'var(--sp-5)',
        marginBottom: 'var(--sp-7)',
      }}
    >
      <KpiTile
        label="Avg approval latency"
        value={fmt(kpis?.approvalLatencyAvgMin)}
        severity={(kpis?.approvalLatencyAvgMin ?? 0) > 120 ? 'urgent' : 'approvals'}
      />
      <KpiTile
        label="P95 approval latency"
        value={fmt(kpis?.approvalLatencyP95Min)}
        severity="approvals"
      />
      <KpiTile
        label="Granted (30d)"
        value={loadErr ? '—' : (kpis?.approvalsGranted ?? '—')}
        severity="approvals"
      />
      <KpiTile
        label="Rejected (30d)"
        value={loadErr ? '—' : (kpis?.approvalsRejected ?? '—')}
        severity={(kpis?.approvalsRejected ?? 0) > (kpis?.approvalsGranted ?? 0) ? 'urgent' : 'approvals'}
      />
    </div>
  );
}
